import type { Database } from 'sql.js';
import { queryOne, type SqlJsBind } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type {
  ComunaEscanMasterRow,
  MasterDataUpdateBundle,
  ParticipanteMasterRow,
  ProveedorMasterRow,
  PuntosEntregaCatalogoMasterRow,
  UsuarioMasterRow,
} from '../../domain/masterDataUpdateBundle.ts';
import {
  upsertParticipanteCatalogRow,
  type ParticipanteCatalogUpsertFields,
} from '../../../shared/participante/infrastructure/participanteCatalogSqlWrite.ts';

function bindParams(params: SqlJsBind[]): SqlJsBind[] {
  return params.map((p) => {
    if (typeof p === 'bigint') return Number(p);
    if (p === undefined || (typeof p === 'number' && !Number.isFinite(p))) return null;
    return p;
  });
}

function resolveComunaId(db: Database, publicId: string): number {
  const trimmed = publicId.trim();
  const row = queryOne(db, 'SELECT id FROM comunas WHERE public_id = ?', [trimmed]);
  if (row?.id != null) return Number(row.id);
  throw new Error(`Master data references unknown comuna_public_id "${publicId}".`);
}

function getTipoDocumentoId(db: Database, publicId: string): number {
  const row = queryOne(db, 'SELECT id FROM tipos_documentos WHERE public_id = ?', [publicId]);
  if (row?.id != null) return Number(row.id);
  db.run('INSERT INTO tipos_documentos (public_id, nombre, estado) VALUES (?, ?, 1)', bindParams([publicId, publicId]));
  const res = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
  return Number(res?.k ?? res?.K ?? 0);
}

/** Resolves or creates `tipos_bonos` from master-download participant fields. */
function resolveTipoBonoIdFromMaster(
  db: Database,
  tipoBonoPublicId: string | null,
  tipoBonoNombre: string | null,
): number | null {
  const publicId = tipoBonoPublicId?.trim() ?? '';
  if (!publicId) return null;
  const existing = queryOne(db, 'SELECT id, nombre FROM tipos_bonos WHERE public_id = ?', [publicId]);
  if (existing?.id != null) {
    const id = Number(existing.id);
    const incomingNombre = tipoBonoNombre?.trim() ?? '';
    const currentNombre = String(existing.nombre ?? '').trim();
    if (incomingNombre && incomingNombre !== currentNombre) {
      db.run(
        `UPDATE tipos_bonos SET nombre = ?, fecha_actualizacion = datetime('now') WHERE id = ?`,
        bindParams([incomingNombre, id]),
      );
    }
    return id;
  }
  const nombre = tipoBonoNombre?.trim() || publicId;
  const byNombre = queryOne(db, 'SELECT id FROM tipos_bonos WHERE nombre = ? COLLATE NOCASE', [nombre]);
  if (byNombre?.id != null) return Number(byNombre.id);
  db.run('INSERT INTO tipos_bonos (public_id, nombre, estado) VALUES (?, ?, 1)', bindParams([publicId, nombre]));
  const res = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
  const k = res?.k ?? res?.K;
  return k != null ? Number(k) : null;
}

function getGrupoAlimentosId(db: Database, nombre: string): number {
  const row = queryOne(db, 'SELECT id FROM grupos_alimentos WHERE nombre = ?', [nombre]);
  if (row?.id != null) return Number(row.id);
  const publicId = 'ga_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
  db.run('INSERT INTO grupos_alimentos (public_id, nombre, estado) VALUES (?, ?, 1)', bindParams([publicId, nombre]));
  const res = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
  return Number(res?.k ?? res?.K ?? 0);
}

function upsertComunaEscan(db: Database, row: ComunaEscanMasterRow): void {
  const existing = queryOne(db, 'SELECT id FROM comunas WHERE public_id = ?', [row.publicId]);
  if (existing?.id != null) {
    db.run(
      `UPDATE comunas SET nombre = ?, estado = ?, latitud = ?, longitud = ?, coordenadas = ? WHERE id = ?`,
      bindParams([
        row.descripcion,
        row.estado,
        row.latitud,
        row.longitud,
        row.coordenadas,
        Number(existing.id),
      ]),
    );
    return;
  }
  db.run(
    `INSERT INTO comunas (public_id, nombre, estado, latitud, longitud, coordenadas)
     VALUES (?, ?, ?, ?, ?, ?)`,
    bindParams([
      row.publicId,
      row.descripcion,
      row.estado,
      row.latitud,
      row.longitud,
      row.coordenadas,
    ]),
  );
}

function upsertParticipante(db: Database, row: ParticipanteMasterRow): void {
  const existing = queryOne(db, 'SELECT id FROM participantes WHERE public_id = ?', [row.publicId]);

  const tipoDocumentoId = getTipoDocumentoId(db, row.tipoDocumentoPublicId);
  const comunaId = resolveComunaId(db, row.comunaPublicId);
  const tipoBonoId = resolveTipoBonoIdFromMaster(db, row.tipoBonoPublicId, row.tipoBonoNombre);
  const fields: ParticipanteCatalogUpsertFields = {
    publicId: row.publicId,
    tipoDocumentoId,
    documento: row.documento,
    nombre: row.nombre,
    telefono: row.telefono,
    celular: row.celular,
    comunaId,
    tipoBonoId,
    estado: row.estado,
  };

  upsertParticipanteCatalogRow(
    db,
    existing?.id != null ? Number(existing.id) : null,
    fields,
    bindParams,
  );
}

function upsertProveedor(db: Database, row: ProveedorMasterRow): void {
  const existing = queryOne(db, 'SELECT id FROM proveedores WHERE public_id = ?', [row.publicId]);
  const lat = row.latitud ?? '';
  const lon = row.longitud ?? '';
  const comunaId = resolveComunaId(db, row.comunaPublicId);

  let proveedorId: number;

  if (existing?.id != null) {
    proveedorId = Number(existing.id);
    db.run(
      `UPDATE proveedores SET nombre = ?, direccion = ?, latitud = ?, longitud = ?, estado = ?, comuna_id = ?,
         fecha_actualizacion = datetime('now') WHERE id = ?`,
      bindParams([row.nombre, row.direccion, lat, lon, row.estado, comunaId, proveedorId]),
    );
  } else {
    db.run(
      `INSERT INTO proveedores (
         public_id, nombre, direccion, latitud, longitud, estado, comuna_id, fecha_creacion, fecha_actualizacion
       ) VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
      bindParams([
        row.publicId,
        row.nombre,
        row.direccion,
        lat,
        lon,
        row.estado,
        comunaId,
      ]),
    );
    const rowId = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
    const k = rowId?.k ?? rowId?.K;
    proveedorId = k != null ? Number(k) : 0;
  }

  if (proveedorId > 0) {
    db.run('DELETE FROM proveedor_grupo_alimentos WHERE proveedor_id = ?', bindParams([proveedorId]));
    const nombres = row.grupoAlimentos.split('\n').map(s => s.trim()).filter(Boolean);
    for (const nombre of nombres) {
      const gaId = getGrupoAlimentosId(db, nombre);
      db.run(
        'INSERT OR IGNORE INTO proveedor_grupo_alimentos (proveedor_id, grupo_alimentos_id) VALUES (?, ?)',
        bindParams([proveedorId, gaId]),
      );
    }
  }
}

function upsertPuntosEntregaCatalogo(db: Database, row: PuntosEntregaCatalogoMasterRow): void {
  const existing = queryOne(db, 'SELECT id FROM puntos_entrega_catalogo WHERE public_id = ?', [row.publicId]);
  const comunaId = resolveComunaId(db, row.comunaPublicId);
  if (existing?.id != null) {
    db.run(
      `UPDATE puntos_entrega_catalogo SET nombre = ?, direccion = ?, comuna_id = ?, estado = ?,
         fecha_actualizacion = datetime('now') WHERE id = ?`,
      bindParams([row.nombre, row.direccion, comunaId, row.estado, Number(existing.id)]),
    );
    return;
  }
  db.run(
    `INSERT INTO puntos_entrega_catalogo (
       public_id, comuna_id, nombre, direccion, estado, fecha_creacion, fecha_actualizacion
     ) VALUES (?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
    bindParams([row.publicId, comunaId, row.nombre, row.direccion, row.estado]),
  );
}

function upsertUsuario(db: Database, row: UsuarioMasterRow): void {
  const existing = queryOne(db, 'SELECT id, password_hash FROM usuarios WHERE public_id = ?', [row.publicId]);
  const incomingPwd = row.passwordHash?.trim() ?? '';
  let passwordHash: string | null = incomingPwd || null;
  if (existing?.id != null) {
    const prevPwd = existing.password_hash != null ? String(existing.password_hash) : '';
    if (!incomingPwd && prevPwd) passwordHash = prevPwd;
  }

  let userId: number;
  if (existing?.id != null) {
    userId = Number(existing.id);
    db.run(
      `UPDATE usuarios SET nombre = ?, nombre_usuario = ?, email = ?, password_hash = ?, estado = ?, fecha_actualizacion = datetime('now')
       WHERE id = ?`,
      bindParams([row.nombre, row.nombreUsuario, row.email, passwordHash ?? '', row.estado, userId]),
    );
  } else {
    db.run(
      `INSERT INTO usuarios (
         public_id, nombre, nombre_usuario, email, password_hash, estado, fecha_creacion, fecha_actualizacion
       ) VALUES (?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
      bindParams([row.publicId, row.nombre, row.nombreUsuario, row.email, passwordHash ?? '', row.estado]),
    );
    const rowId = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
    const k = rowId?.k ?? rowId?.K;
    userId = k != null ? Number(k) : 0;
  }

  if (userId > 0 && row.permiso) {
    const permisoId = parseInt(row.permiso, 10);
    if (!isNaN(permisoId)) {
      db.run('DELETE FROM usuario_rol WHERE usuario_id = ?', bindParams([userId]));
      db.run('INSERT OR IGNORE INTO usuario_rol (usuario_id, rol_id) VALUES (?, ?)', bindParams([userId, permisoId]));
    }
  }
}

const APPLY_PROGRESS_YIELD_EVERY = 25;

async function yieldToUi(): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
}

export async function applyMasterDataBundleToDb(
  db: Database,
  bundle: MasterDataUpdateBundle,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{
  comunasEscan: number;
  participantes: number;
  proveedores: number;
  usuarios: number;
  puntosEntregaCatalogo: number;
}> {
  const totalRows =
    bundle.comunasEscan.length +
    bundle.participantes.length +
    bundle.proveedores.length +
    bundle.usuarios.length +
    bundle.puntosEntregaCatalogo.length;
  let loadedRows = 0;
  const reportProgress = async (): Promise<void> => {
    if (totalRows <= 0) return;
    const isLast = loadedRows === totalRows;
    if (!isLast && loadedRows % APPLY_PROGRESS_YIELD_EVERY !== 0) return;
    onProgress?.(loadedRows, totalRows);
    await yieldToUi();
  };

  db.run('BEGIN IMMEDIATE');
  try {
    for (const row of bundle.comunasEscan) {
      upsertComunaEscan(db, row);
      loadedRows += 1;
      await reportProgress();
    }
    for (const row of bundle.participantes) {
      upsertParticipante(db, row);
      loadedRows += 1;
      await reportProgress();
    }
    for (const row of bundle.proveedores) {
      upsertProveedor(db, row);
      loadedRows += 1;
      await reportProgress();
    }
    for (const row of bundle.usuarios) {
      upsertUsuario(db, row);
      loadedRows += 1;
      await reportProgress();
    }
    for (const row of bundle.puntosEntregaCatalogo) {
      upsertPuntosEntregaCatalogo(db, row);
      loadedRows += 1;
      await reportProgress();
    }
    db.run('COMMIT');
    return {
      comunasEscan: bundle.comunasEscan.length,
      participantes: bundle.participantes.length,
      proveedores: bundle.proveedores.length,
      usuarios: bundle.usuarios.length,
      puntosEntregaCatalogo: bundle.puntosEntregaCatalogo.length,
    };
  } catch (e) {
    db.run('ROLLBACK');
    throw e;
  }
}
